import type { Context } from "@/types";

/**
 * Default title from capture context when the user has not typed a title and
 * there is no selection (browser tab name, file basename, or app name).
 */
export function captureContextFallbackTitle(ctx: Context): string | undefined {
  switch (ctx.category) {
    case "browser":
      return ctx.tabName;
    case "file":
      return ctx.fileName?.replace(/\.[^.]+$/, "");
    case "app":
      return ctx.appName;
  }
}

/**
 * Default object title from capture: trimmed selection when present, otherwise
 * {@link captureContextFallbackTitle} (tab name, file basename, app name).
 */
export function captureContextDefaultTitle(ctx: Context): string | undefined {
  const sel = ctx.selection?.trim();
  if (sel) return sel;
  return captureContextFallbackTitle(ctx);
}

/**
 * One-line display text for an attached capture context (action rows, console
 * context pill): the cleaned-up url, file name/path, or app name. The
 * selection is deliberately never displayed (it still serves as the default
 * capture title, see {@link captureContextDefaultTitle}): the pill describes
 * where the capture comes from, not what it will be titled.
 */
export function captureContextDisplayText(ctx: Context): string {
  switch (ctx.category) {
    case "browser":
      return ctx.url
        ? ctx.url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")
        : ctx.appName || "";
    case "file":
      return ctx.fileName || ctx.path || ctx.appName || "";
    case "app":
      return ctx.appName || "";
  }
}

/**
 * Whether the context describes the Routine desktop app itself.
 */
function isRoutineSelfContext(ctx: Context): boolean {
  return ctx.category === "app" && ctx.appName === "Routine";
}

/**
 * Whether capture context should be sent to the dashboard (vs stripped).
 * Keeps browser and app captures, file captures when path or filename exists,
 * or any category when there is a non-empty selection.
 * Always strips context originating from the Routine desktop app itself.
 */
export function captureContextShouldRetain(ctx: Context | undefined): boolean {
  if (!ctx) return false;
  if (isRoutineSelfContext(ctx)) return false;
  if (ctx.selection?.trim()) return true;
  switch (ctx.category) {
    case "browser":
    case "app":
      return true;
    case "file":
      return !!(ctx.fileName || ctx.path);
  }
}
